import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Trophy, Zap, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Logo } from '@/components/Logo';
import { SeoHead } from '@/components/SeoHead';


interface Inviter {
  username: string;
  level: number;
  xp: number;
  preferred_sport: string;
}

const SPORT_EMOJI: Record<string, string> = {
  football: '🏈',
  soccer: '⚽',
  basketball: '🏀',
  baseball: '⚾',
};

export default function InvitePage() {
  const { code } = useParams<{ code: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [inviter, setInviter] = useState<Inviter | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!code) { setNotFound(true); setLoading(false); return; }

    supabase.rpc('get_inviter_by_code', { p_code: code }).then(({ data, error }) => {
      if (error || !data || (data as any[]).length === 0) {
        setNotFound(true);
      } else {
        const row = (data as any[])[0];
        setInviter(row);
        // Store only the invite code; UUID is resolved server-side at follow time.
        localStorage.setItem('pending_invite', JSON.stringify({ code }));
      }
      setLoading(false);
    });
  }, [code]);

  // If user is already logged in, process the invite and redirect
  useEffect(() => {
    if (!user || !inviter || !code) return;

    const processFollow = async () => {
      await supabase.rpc('follow_by_invite_code' as any, { p_code: code });
      localStorage.removeItem('pending_invite');
      navigate('/', { replace: true });
    };

    processFollow();
  }, [user, inviter, code, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Logo className="w-12 h-12 animate-pulse" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <Logo className="w-12 h-12 mx-auto" alt="Sportara" />
          <h1 className="font-display font-extrabold text-2xl">Invalid Invite Link</h1>
          <p className="text-muted-foreground font-body text-sm">This invite link is no longer valid or doesn't exist.</p>
          <Button onClick={() => navigate('/')} className="rounded-xl font-display font-bold">
            Go to Sportara
          </Button>
        </div>
      </div>
    );
  }

  const sportEmoji = SPORT_EMOJI[inviter?.preferred_sport || 'football'] || '🏈';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <SeoHead
        title={inviter ? `Join ${inviter.username} on Sportara` : 'You\'re invited to Sportara'}
        description="Accept your invite to start learning sports strategy with friends on Sportara."
        path={`/invite/${code ?? ''}`}
        noindex
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm space-y-6"
      >
        {/* Logo */}
        <div className="text-center">
          <Logo className="w-14 h-14 mx-auto mb-2" alt="Sportara" />
          <p className="text-xs text-muted-foreground font-body">Train Your Game IQ</p>
        </div>

        {/* Invite Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-card rounded-2xl border-2 border-border p-6 shadow-card text-center space-y-4"
        >
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <Users className="w-8 h-8 text-primary" />
          </div>

          <div>
            <h1 className="font-display font-extrabold text-xl">
              {inviter?.username} invited you!
            </h1>
            <p className="text-sm text-muted-foreground font-body mt-1">
              Join Sportara and compete with friends
            </p>
          </div>

          {/* Inviter stats */}
          <div className="flex items-center justify-center gap-4 py-3 bg-muted/50 rounded-xl">
            <div className="text-center">
              <p className="text-lg">{sportEmoji}</p>
              <p className="text-[10px] text-muted-foreground font-body capitalize">{inviter?.preferred_sport}</p>
            </div>
            <div className="w-px h-8 bg-border" />
            <div className="text-center">
              <p className="font-display font-extrabold text-sm flex items-center gap-1">
                <Trophy className="w-3.5 h-3.5 text-accent" /> Level {inviter?.level}
              </p>
              <p className="text-[10px] text-muted-foreground font-body">{inviter?.xp} XP</p>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-3"
        >
          <Button
            onClick={() => navigate('/')}
            className="w-full h-14 rounded-2xl font-display font-bold text-base"
          >
            <Zap className="w-5 h-5 mr-2" />
            Join Sportara
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-center text-xs text-muted-foreground font-body">
            Free to play · No credit card needed
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
